<template>
  <section>
    <v-btn
      dark
      color="primary"
      class="media-kit-add-btn"
      @click="openModal('add')"
      v-if="isWriteAvailable"
    >
      Add media kit
    </v-btn>

    <!-- MEDIA KITS LIST -->
    <div v-if="getMediaKits.length">
    <v-card class="media-kit"
      v-for="(mediaKit, i) in getMediaKits"
      :key="`media-kit-${i}`"
    >
      <div class="media-kit__info">
        <h3>{{ getName(mediaKit) }}</h3>
      </div>
      <div class="media-kit__controls">
        <div class="media-kit__controls-status">
          <v-switch
            v-if="getStatus(mediaKit)"
            :label="`Status: ${getStatus(mediaKit)}`"
            v-model="mediaKit.mediaKitStatus"
            false-value="Disable"
            true-value="Enable"
            @click.native="changeStatus(mediaKit)"
            :disabled="!isWriteAvailable"
          />
        </div>
        <v-btn color="primary" fab small dark @click="openModal('edit', mediaKit)" :disabled="!isWriteAvailable">
          <v-icon>edit</v-icon>
        </v-btn>
        <v-btn color="error" fab small dark @click="removeMediaKit(mediaKit)" :disabled="!isWriteAvailable">
          <v-icon>delete</v-icon>
        </v-btn>
      </div>
    </v-card>
    </div>
    <!-- MEDIA KITS LIST -->

    <!-- FORM MODAL -->
    <v-dialog persistent v-model="isModalVisible" max-width="500px">
      <v-card>
        <v-card-title>
          <h2 v-if="modalMode === 'add'">Add media kit</h2>
          <h2 v-else>Edit media kit {{ getName(mediaKitForm) }}</h2>
        </v-card-title>
        <v-card-text>
          <!-- name -->
          <content-field
            label="Name"
            v-model="mediaKitForm.name"
            :error="$v.mediaKitForm.name"
          />
          <!-- name -->

          <!-- status -->
          <v-switch
            :label="`Status: ${mediaKitForm.mediaKitStatus}`"
            v-model="mediaKitForm.mediaKitStatus"
            false-value="Disable"
            true-value="Enable"
          />
          <!-- status -->

          <!-- pdf file -->
          <div class="pdf-upload">
            <div v-if="mediaKitForm.pdfFileId">
              <span><strong>Attached PDF:</strong></span>
              <a :href="pdfLink" target="_blank">{{ pdfName }}</a>
            </div>
            <e-upload
              :extensions="['.pdf']"
              :directUpload="false"
              btnText="Select PDF"
              padding="pa-0"
              class="pdf-upload__btn"
              @filePicked="onPdfPicked"
            />
            <div
              v-if="isPdfInvalid"
              class="pdf-upload__error"
            >
              This field is required
            </div>
          </div>
          <!-- pdf file -->

          <!-- email template -->
          <v-select
            :items="mediaKitTemplatesList"
            v-model="mediaKitForm.emailTemplateKey"
            :error-messages="isValid('emailTemplateKey')"
            label="Select e-mail template"
            item-value="templateKey"
            item-text="templateKey"
          />
          <!-- email template -->
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="success" @click="saveForm">Save</v-btn>
          <v-btn color="primary" @click="closeModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- FORM MODAL -->
  </section>
</template>

<script>
  import { pathOr } from 'ramda'
  import EUpload from '@/components/shared/Upload'
  import { required } from 'vuelidate/lib/validators'
  import { mapActions, mapGetters } from 'vuex'
  import mixins from '@/utils/mixins'

  export default {
    name: 'SponsorsMediaKits',
    mixins: [mixins.isWriteAvailable, mixins.emailTemplates],
    components: {
      EUpload
    },
    data () {
      return {
        isModalVisible: false,
        modalMode: '',
        editableItemId: '',
        mediaKitForm: {
          name: '',
          mediaKitStatus: 'Disable',
          pdfFileId: '',
          emailTemplateKey: ''
        },
        // DON'T DELETE! need for isWriteAvailable mixin
        scopeForWrite: 'sponsors:write'
      }
    },
    validations: {
      mediaKitForm: {
        name: { required },
        pdfFileId: { required },
        emailTemplateKey: { required }
      }
    },
    computed: {
      ...mapGetters([
        'getMediaKits',
        'getFileById'
      ]),
      pdfName () {
        return pathOr('File link', ['fileName'], this.getFileById(this.mediaKitForm.pdfFileId))
      },
      pdfLink () {
        return pathOr('', ['fileUrl'], this.getFileById(this.mediaKitForm.pdfFileId))
      },
      isPdfInvalid () {
        return this.$v.mediaKitForm.pdfFileId.$dirty && this.$v.mediaKitForm.pdfFileId.$invalid
      }
    },
    methods: {
      ...mapActions([
        'fetchMediaKits',
        'createMediaKit',
        'updateMediaKit',
        'deleteMediaKit',
        'fetchFiles',
        'uploadFileToStorage'
      ]),
      async onPdfPicked (file) {
        try {
          const fileId = await this.uploadFileToStorage(file)
          this.mediaKitForm.pdfFileId = fileId
        } catch (err) {
          console.error('Failed to upload pdf')
          console.error(err)
        }
      },
      isValid (fieldName) {
        return this.$v.mediaKitForm[fieldName].$dirty && this.$v.mediaKitForm[fieldName].$invalid
          ? ['This field is required']
          : []
      },
      getName (mediaKit) {
        return pathOr('', ['name'], mediaKit)
      },
      getStatus (mediaKit) {
        return pathOr('', ['mediaKitStatus'], mediaKit)
      },
      openModal (mode, editableMediaKit) {
        this.resetForm()
        this.modalMode = mode
        this.isModalVisible = true

        if (mode === 'edit' && editableMediaKit) {
          this.editableItemId = editableMediaKit.id
          this.setForm(editableMediaKit)
        }
      },
      closeModal () {
        this.isModalVisible = false
        this.modalMode = null
        this.editableMediaKit = null
        this.editableItemId = ''
      },
      setForm (formData) {
        this.mediaKitForm = { ...formData }
      },
      resetForm () {
        this.mediaKitForm.name = ''
        this.mediaKitForm.mediaKitStatus = 'Disable'
        this.mediaKitForm.pdfFileId = ''
        this.mediaKitForm.emailTemplateKey = ''
        this.$v.$reset()
      },
      saveForm () {
        this.$v.$touch()
        if(this.$v.mediaKitForm.$invalid) return

        this.modalMode === 'add'
          ? this.addNewMediaKit()
          : this.editMediaKit()
      },
      async addNewMediaKit () {
        try {
          await this.createMediaKit(this.mediaKitForm)
          this.closeModal()
        } catch (err) {
          throw err
        }
      },
      async editMediaKit () {
        const payload = {
          id: this.editableItemId,
          ...this.mediaKitForm
        }
        try {
          await this.updateMediaKit(payload)
          this.closeModal()
        } catch (err) {
          throw err
        }
      },
      changeStatus ({ id, mediaKitStatus }) {
        this.updateMediaKit({ id, mediaKitStatus })
      },
      removeMediaKit (mediaKit) {
        if (!pathOr(false, ['id'], mediaKit)) {
          console.error(`Can't find mediakit ID in`, mediaKit)
          return
        }

        this.deleteMediaKit(mediaKit.id)
      }
    },
    async created () {
      try {
        await this.fetchMediaKits()
        await this.fetchFiles()
      } catch (err) {
        console.warn(err)
      }
    }
  }
</script>

<style lang="scss" scoped>
  .media-kit {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    margin: 15px 0;

    &__controls {
      display: flex;
      align-items: center;

      &-status {
        height: 30px;
        min-width: 165px;
      }
    }

    &-add-btn {
      margin-left: 0;
    }

    @media screen and (max-width: 400px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .pdf-upload {
    margin-bottom: 25px;

    &__btn {
      /deep/ .btn {
        margin-left: 0;
      }
    }

    &__error {
      font-size: 12px;
      color: #ff5252;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/Sponsors/Settings/SponsorsMediaKits/index.vue